import React from "react";
import clsx, { type ClassValue, } from "clsx";
import { twMerge, } from "tailwind-merge";

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export interface FormHelperTextProps extends React.HTMLAttributes<HTMLParagraphElement> {
  /**
   * O componente usado para o nó raiz.
   * @default 'p'
   */
  component?: React.ElementType;
  /**
   * Se `true`, exibe o texto de ajuda no estado desabilitado (cinza mais claro).
   */
  disabled?: boolean;
  /**
   * Se `true`, exibe o texto de ajuda no estado de erro (vermelho).
   */
  error?: boolean;
  /**
   * Se `true`, usa as classes da variante filled (geralmente injetado via contexto pelo FormControl).
   */
  filled?: boolean;
  /**
   * Se `true`, usa as classes de foco.
   */
  focused?: boolean;
  /**
   * Ajusta o espaçamento vertical (margem superior).
   */
  margin?: "dense";
  /**
   * Se `true`, denota que o campo atrelado é obrigatório (apenas para compatibilidade de API).
   */
  required?: boolean;
  /**
   * A variante utilizada dita o espaçamento horizontal.
   * Em outlined/filled, adicionamos uma margem lateral para alinhar com o padding do input.
   * @default 'standard'
   */
  variant?: "standard" | "outlined" | "filled";
}

const FormHelperText = React.forwardRef<HTMLParagraphElement, FormHelperTextProps>((props, ref) => {
  const {
    children,
    className,
    component: Component = "p",
    disabled,
    error,
    margin,
    variant = "standard",
    ...other
  } = props;

  // Se o conteúdo for exatamente um espaço em branco (' '), rendemos um espaço
  // inquebrável para garantir que o HTML não colapse a altura da linha.
  const content = children === " " ? "\u00A0" : children;

  return (
    <Component
      ref={ref}
      className={cn(
        // Estilos base de tipografia
        "text-xs text-left transition-colors duration-200",

        // Espaçamento vertical
        margin === "dense" ? "mt-1" : "mt-1.5",

        // Cores baseadas no estado
        error ? "text-red-600" : "text-slate-500",
        disabled && "text-slate-400",

        // Lógica de Variante: 'outlined' e 'filled' costumam ter margem
        // horizontal (14px = mx-3.5) para alinhar perfeitamente com o texto do Input.
        (variant === "outlined" || variant === "filled") && "mx-3.5",

        className
      )}
      {...other}
    >
      {content}
    </Component>
  );
});

FormHelperText.displayName = "FormHelperText";

export default FormHelperText;